import { useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';
import settings from '/@/core/settings';
import {
    getCatalogItemFromApi,
    getCatalogItemsByBrandFromApi,
    getRTVCatalogItemsFromApi,
} from '/@/store/catalog/apiProviders';
import { axFetch } from '/@/store/session/actions';

export const useAllCatalogProducts = (
    {
        brand,
        crop,
        country,
        cropProtectionCrop,
        cropProtectionProductType,
        target = '',
        sortField,
        zone,
        technology,
        densityCoefficientsEnvironment,
        zonesPlantingDate,
        published = true,
        zoneIds,
        page = 1,
        pageSize = 10000,
    }: {
        brand: string;
        crop: string;
        country: string;
        densityCoefficientsEnvironment?: number;
        cropProtectionCrop?: string;
        cropProtectionProductType?: string;
        target?: string;
        sortField?: string;
        zone?: number;
        page?: number;
        technology?: number;
        pageSize?: number;
        zonesPlantingDate?: string;
        published?: boolean;
        zoneIds?: number[];
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    let key = {
        key: 'allCatalogProducts',
        brand,
        crop,
        country,
        cropProtectionCrop,
        cropProtectionProductType,
        target,
        sortField,
        zone,
        page,
        pageSize,
        technology,
        densityCoefficientsEnvironment,
        zonesPlantingDate,
        published,
        zoneIds,
    };
    if (!enabled || !brand || !country || !crop) key = null;

    return useSWRImmutable(key, getCatalogItemsByBrandFromApi);
};

export const useCatalogProductById = ({ id, crop }, { enabled = true }: { enabled?: boolean } = { enabled: true }) => {
    const { cache } = useSWRConfig();
    let key = {
        key: 'catalogProduct',
        id,
        crop,
    };
    if (!enabled || !id || !crop) key = null;
    return useSWRImmutable(key, ({ id, crop }) =>
        getCatalogItemFromApi(
            {
                key: 'catalogProduct',
                id,
                crop,
            },
            cache
        )
    );
};

export const useCatalogProductsById = ({ ids, crop }: { ids: number[]; crop: string }) => {
    const { cache } = useSWRConfig();

    const key = ids.length > 0 && crop ? { key: 'catalogProducts', ids, crop } : null;

    const { data, error, isValidating } = useSWRImmutable(key, async () => {
        const results = await Promise.all(
            ids.map(async (id) => {
                const result = await getCatalogItemFromApi({ id, crop }, cache);
                return { id, result };
            })
        );

        return results.map(({ result }) => result);
    });

    return {
        data,
        error,
        isValidating,
    };
};

export const useRTVCatalogProducts = ({
    user,
    country,
    target,
    sortField,
    cropProtectionCrop,
    cropProtectionProductType,
    appInstance,
    crop,
    page = 1,
    pageSize = 10000,
}: {
    user: any;
    country: string;
    target: string;
    sortField: string;
    cropProtectionCrop: string;
    cropProtectionProductType: string;
    appInstance: any;
    crop: string;
    page?: number;
    pageSize?: number;
}) => {
    const { cache } = useSWRConfig();
    let key = {
        key: 'allRTVCatalogProducts',
        user,
        country,
        target,
        sortField,
        cropProtectionCrop,
        cropProtectionProductType,
        appInstance,
        crop,
        page,
        pageSize,
    };
    if (crop !== 'crop_protection') key = null;
    return useSWRImmutable(
        key,
        ({
            country,
            cropProtectionCrop,
            cropProtectionProductType,
            target,
            sortField,
            user,
            appInstance,
            page,
            pageSize,
        }) =>
            getRTVCatalogItemsFromApi(
                {
                    key: 'allRTVCatalogProducts',
                    country,
                    cropProtectionCrop,
                    cropProtectionProductType,
                    target,
                    sortField,
                    user,
                    appInstance,
                    page,
                    pageSize,
                },
                cache
            )
    );
};

const getCornTechnologiesFromApi = async ({
    page,
    pageSize = 10,
    brand,
    country,
}: {
    page: number;
    pageSize?: number;
    brand?: string;
    country?: string;
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/corn/technologies/`, window.location.origin);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    if (brand) params.append('brand', brand);
    if (country) params.append('country', country);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useCornTechnologies = ({
    page,
    pageSize = 10,
    brand,
    country,
}: {
    page: number;
    pageSize?: number;
    brand?: string;
    country?: string;
}) => {
    const key = { key: 'useCornTechnologies', page, pageSize, brand, country };
    return useSWRImmutable(key, getCornTechnologiesFromApi);
};

const getSoyTechnologiesFromApi = async ({
    page,
    pageSize = 10,
    brand,
    country,
}: {
    page: number;
    pageSize?: number;
    brand?: string;
    country?: string;
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/soy/technologies/`, window.location.origin);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    if (brand) params.append('brand', brand);
    if (country) params.append('country', country);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useSoyTechnologies = ({
    page,
    pageSize = 10,
    brand,
    country,
}: {
    page: number;
    pageSize?: number;
    brand?: string;
    country?: string;
}) => {
    const key = { key: 'useSoyTechnologies', page, pageSize, brand, country };
    return useSWRImmutable(key, getSoyTechnologiesFromApi);
};

export const useGetCatalogInternalComparisonChoicesCropProtection = (item, isInternal) => {
    if (!item || !isInternal) return [];
    return item.rtvOthers.map((i) => {
        return {
            value: i.id,
            label: i.name,
        };
    });
};
